import React, { useCallback, useMemo, useRef } from 'react';
import { useVirtual } from 'react-virtual';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  Menu,
  MultiAutocompleteTokenControl,
  Placeholder,
  useMultiAutocomplete,
  VirtualizedMenuItem,
} from '@adsk/alloy-react-dropdown';
import Footer, { ClearButton } from '@adsk/alloy-react-footer';
import PropTypes from 'prop-types';

const RolesDropdown = ({
  isLoadingRoles,
  onRolesChange,
  roles,
  selectedRoles,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const orderedRoles = useMemo(() => {
    return (
      selectedRoles.length > 0
        ? [
            ...roles.filter((role) =>
              selectedRoles.some((selectedRole) => role.id === selectedRole.id)
            ),
            ...roles.filter((role) =>
              selectedRoles.every((selectedRole) => role.id !== selectedRole.id)
            ),
          ]
        : roles
    ).map((role) => ({ value: role.id, label: role.name }));
  }, [roles, selectedRoles]);
  const selectedItems = useMemo(() => {
    return selectedRoles.length > 0
      ? orderedRoles.filter((orderedRole) =>
          selectedRoles.some(
            (selectedRole) => selectedRole.id === orderedRole.value
          )
        )
      : [];
  }, [orderedRoles, selectedRoles]);

  const {
    closeMenu,
    controlProps,
    menuProps: allMenuProps,
  } = useMultiAutocomplete({
    id: 'ProjectMemberFilterPanel_Roles_MultiAutocomplete',
    items: orderedRoles,
    onSelectedItemsChange: ({ selectedItems: selected }) => {
      onRolesChange(selected);
    },
    selectedItems,
  });

  const { menuItems, getItemProps, ...menuProps } = allMenuProps;

  const parentRef = useRef(null);

  const rowVirtualizer = useVirtual({
    size: menuItems.length,
    parentRef,
    estimateSize: useCallback(
      (index) => (menuItems[index].subtext ? 56 : 36),
      [menuItems]
    ),
    overscan: 10,
  });

  return (
    <>
      <MultiAutocompleteTokenControl
        {...controlProps}
        renderPlaceholder={() => (
          <Placeholder>{T.translate('ROLE_PLACEHOLDER')}</Placeholder>
        )}
      />
      <Menu
        {...menuProps}
        isLoading={isLoadingRoles}
        renderFooter={() => (
          <Footer>
            <ClearButton
              onClick={() => {
                onRolesChange(null);
                closeMenu();
              }}
            />
          </Footer>
        )}
        renderWrapper={({ style, ...wrapperProps }) => (
          <div
            ref={parentRef}
            style={{
              height: rowVirtualizer.totalSize,
              ...style,
              overflowX: 'hidden',
            }}
            {...wrapperProps}
          />
        )}
      >
        {rowVirtualizer.virtualItems.map(({ index, size, start }) => (
          <VirtualizedMenuItem
            key={index}
            start={start}
            height={size}
            {...getItemProps({ item: menuItems[index] })}
            data-testid={`Dropdown__MenuItem--${index}`}
          />
        ))}
      </Menu>
    </>
  );
};

RolesDropdown.propTypes = {
  isLoadingRoles: PropTypes.bool.isRequired,
  onRolesChange: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  selectedRoles: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any))
    .isRequired,
};

export default RolesDropdown;
